import PropTypes from "prop-types";
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

import { makeStyles } from "tss-react/mui";

import CircularProgress from "@mui/material/CircularProgress";

import { setPrevState } from "../../../util/common";
import DataExplorerService from "../../../service/dataExplorer";
import { statCols, statColsMini } from "./statisticsData";

const useStyles = makeStyles()((theme) => ({
  BrowserTableContainer: {
    marginBottom: "12px",
    maxWidth: "99%",
    paddingLeft: "10px",
    "& .MuiTableCell-head": {
      fontWeight: "600 !important",
    },
    "&::-webkit-scrollbar": {
      width: "6px",
      height: "6px",
    },
    "&::-webkit-scrollbar-track": {
      borderRadius: "10px",
    },
  },
  selectableRow: {
    cursor: "pointer",
    "&:hover": {
      background: "rgba(0, 0, 0, 0.01)",
    },
  },
  selectedRow: {
    background: "rgba(0, 0, 0, 0.1)",
    color: "white",
    "&:hover": {
      background: "rgba(0, 0, 0, 0.1)",
    },
  },
  fontsize: {
    fontSize: "12px",
  },
}));

const Columns = ({ Workspace_id, fromPopup, tableName, schemaName, selectedRow, setState }) => {
  const { classes } = useStyles();

  const [loading, setLoading] = useState(false);

  const dataRef = useRef({
    columns: [],
    rowsCount: 0,
    columnCount: 0,
    sampleSize: 0,
  });

  const { columns, rowsCount, columnCount, sampleSize } = dataRef.current;

  const $columns = useMemo(() => (fromPopup ? statCols : statColsMini), [fromPopup]);

  useEffect(() => {
    const dataExplorerService = new DataExplorerService();
    const getStatistics = async () => {
      const response = await dataExplorerService.getStatistics(Workspace_id, {
        schema: schemaName,
        table: tableName,
      });

      if (response?.success) {
        dataRef.current = {
          columns: response.data.columns,
          rowsCount: response.data.rowsCount,
          columnCount: response.data.columnCount,
          sampleSize: response.data.sampleSize,
        };

        setPrevState(setState, {
          selectedRow: response.data.columns.length ? response.data.columns[0]["CLM_NAME"] : "",
          selectedType: response.data.columns.length ? response.data.columns[0]["DATATYPE"] : "",
          currentTableName: tableName,
        });
      }

      return response?.data?.columnCount || 0;
    };

    setLoading(true);
    //Loading data of first 100 columns
    getStatistics()
      .then(() => setLoading(false))
      .catch(() => setLoading(false));

    return () => dataExplorerService.cancelRequest();
  }, [Workspace_id, schemaName, setState, tableName]);

  const TableRows = useCallback(() => {
    if (loading || $columns.length === 0)
      return (
        <TableRow>
          <TableCell
            style={{
              borderBottom: "0px",
              textAlign: "center",
              minHeight: "180px",
              height: "180px",
              alignItems: "center",
            }}
            rowSpan={5}
            colSpan={$columns?.length}
            className={classes.fontsize}
          >
            {loading ? <CircularProgress /> : "No records found"}
          </TableCell>
        </TableRow>
      );

    return columns.map((val) => (
      <TableRow
        key={val["CLM_NAME"]}
        onClick={() => {
          setPrevState(setState, { selectedRow: val["CLM_NAME"], selectedRowType: val["DATATYPE"] });
        }}
        sx={{ wordBreak: "break-all" }}
        className={selectedRow === val["CLM_NAME"] ? classes.selectedRow : classes.selectableRow}
      >
        {$columns.map((col) => {
          if (col.id === "MIN_VALUE")
            return (
              <TableCell key={col.id} sx={{ maxWidth: col.minWidth }} className={classes.fontsize}>
                {val["DATATYPE"].startsWith("VARCHAR") || val["DATATYPE"] === "BOOLEAN" ? "-" : val["MIN_VALUE"]}
              </TableCell>
            );
          else if (col.id === "MAX_VALUE")
            return (
              <TableCell key={col.id} sx={{ maxWidth: col.minWidth }} className={classes.fontsize}>
                {val["DATATYPE"].startsWith("VARCHAR") || val["DATATYPE"] === "BOOLEAN" ? "-" : val["MAX_VALUE"]}
              </TableCell>
            );
          else
            return (
              <TableCell key={col.id} sx={{ maxWidth: col.minWidth }} className={classes.fontsize}>
                {val[col.id]}
              </TableCell>
            );
        })}
      </TableRow>
    ));
  }, [$columns, classes.fontsize, classes.selectableRow, classes.selectedRow, columns, loading, selectedRow, setState]);

  return (
    <>
      <Box sx={{ py: 1 }} display={"flex"} justifyContent="space-evenly">
        <Typography>
          <span style={{ fontWeight: "bold", fontSize: "14px" }}> Number of Rows: </span> {rowsCount}
        </Typography>
        <Typography>
          <span style={{ fontWeight: "bold", fontSize: "14px" }}> Number of Columns: </span> {columnCount}
        </Typography>
        {sampleSize < rowsCount && (
          <Typography>
            <span style={{ fontSize: "12px" }}>*Statistics is based on {sampleSize} random records</span>
          </Typography>
        )}
      </Box>
      <TableContainer sx={{ height: fromPopup ? 200 : 250 }} className={classes.BrowserTableContainer}>
        <Table size="small" stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              {$columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{ minWidth: column.minWidth }}
                  className={classes.fontsize}
                >
                  {column.name}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>{TableRows()}</TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

Columns.propTypes = {
  Workspace_id: PropTypes.string,
  fromPopup: PropTypes.bool,
  schemaName: PropTypes.string,
  selectedRow: PropTypes.string,
  setState: PropTypes.func,
  tableName: PropTypes.string,
};

export default React.memo(Columns);
